import React, { ReactElement, useContext, useEffect, useState } from "react";
import { Box, createStyles, Grid, makeStyles, Theme } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AxiosResponse } from "axios";
import { useHistory } from "react-router-dom";
import CustomProgress from "../../../components/Alert/CustomProgress";
import LinearIndeterminate from "../../../components/Loading/LinearIndeterminate";
import CustomButton from "../../../components/Button/CustomButton";
import { tesaBlue, tesaGrey5 } from "../../../styles/colors";
import { TestRequestDisplayedStateStatus } from "../../../components/Chip/StatusChip";
import { TestRequestSearchResult } from "../../../api/models/TestRequest";
import TestRequestCard from "./TestRequestCard";
import windowOpenHelper from "../../../helper/WindowHelper";
import { UserContext } from "../../../contexts/UserContext";
import PagedResponse from "../../../api/models/PagedResponse";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import WaitingFilter from "../../../api/models/WaitingFilter";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { TestRequestAdvancedSearchRQ } from "../../../api/models/advanced-search/TestRequestAdvancedSearchRQ";
import PrioritySearchTerm from "../../../api/models/test-request/PrioritySearchTerm";

type LocalTestRequestSearchResult = Omit<TestRequestRaw.Schemas.TestRequestSearchResult, "status"> & {
  status: TestRequestDisplayedStateStatus;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: "16px",
      minWidth: "896px",
    },
    buttonOrSpinnerContainer: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(6),
    },
    cardContainer: {
      borderTop: `1px solid ${tesaGrey5}`,
      padding: "12px 16px",
    },
    column: {
      padding: "0px !important",
      border: `1px solid ${tesaGrey5}`,
      "&:first-child": {
        borderRightWidth: 0,
      },
      "&:nth-child(2)": {
        borderRightWidth: 0,
      },
      "&:nth-child(3)": {
        borderRightWidth: 0,
      },
    },
    h3: {
      marginBottom: "0px",
      marginTop: "0px",
      padding: "12px 16px",
    },
  }),
);

interface TestRequestBoardProps {
  permission: boolean;
  refresh: boolean;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  waiting: boolean;
  waitingForCustomer?: WaitingFilter;
  statusFilterQuery?: string;
  fetchTestRequests: () => Promise<AxiosResponse<PagedResponse<TestRequestSearchResult>>>;
  sync: <T>(fetch: () => Promise<T>) => Promise<T>;
  basicSearchTerms?: string[];
  prioritySearchTerm: PrioritySearchTerm;
  advancedSearch?: TestRequestAdvancedSearchRQ;
  retriggerSearch?: boolean;
  pageIndex: number;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  changeRequestPriority?: (id: string, priority: string) => void;
}

const TestRequestBoard = ({
  permission: editPriority,
  refresh,
  isLoading,
  setIsLoading,
  waiting,
  waitingForCustomer,
  statusFilterQuery,
  fetchTestRequests,
  sync,
  basicSearchTerms,
  prioritySearchTerm,
  advancedSearch,
  retriggerSearch,
  pageIndex,
  setPageIndex,
  pageSize,
  changeRequestPriority,
}: TestRequestBoardProps): ReactElement => {
  const classes = useStyles();
  const { user } = useContext(UserContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const requesterView = user?.roles?.length === 1 && user?.roles?.includes("LEAP_Requester");
  const [doReload, setDoReload] = useState(0);
  const [isReloading, setIsReloading] = useState(false);
  const history = useHistory();
  const { labOrUser } = useContext(DashboardContext);
  const [testRequests, setTestRequests] = useState<LocalTestRequestSearchResult[]>([]);
  const [totalData, setTotalData] = useState<number>(testRequests.length || 0);
  const showCollapseButton = totalData && totalData > pageSize && pageIndex > 0;
  const showLoadMoreButton = totalData && totalData > pageSize * pageIndex + pageSize;
  const showButtons = !isLoading && (showCollapseButton || showLoadMoreButton);

  const handleMultiTab = (req: LocalTestRequestSearchResult) => {
    windowOpenHelper({
      url: `requests/${req.requestId}`,
    });
  };

  const openTestRequest = (req: LocalTestRequestSearchResult) => {
    localStorage.setItem("recentReqId", req.requestId as string);
    localStorage.setItem("recentDialog", "");
    history.push(`/requests/${req.requestId}`);
  };

  const loadMore = () => {
    setIsLoading(true);
    setPageIndex((previousPageIndex) => previousPageIndex + 1);
  };

  const collapse = () => {
    setIsLoading(true);
    setTestRequests([]);
    setPageIndex(0);
  };

  const cardFromRequest = (req: LocalTestRequestSearchResult): JSX.Element => {
    return (
      <TestRequestCard
        permission={editPriority}
        assignee={req.assignee?.userName || ""}
        key={req.id}
        priority={req.testRequestPriority || ""}
        customerClassification={req.customerClassification || ""}
        pnsPotential={req.pnsPotential?.value.toString() || ""}
        customer={req.directCustomer || ""}
        deadline={req.deadLine || ""}
        estimatedFinishingDate={req.estimatedFinishingDate || ""}
        flags={req.flags || []}
        name={req.fullTitle || ""}
        onCardClick={() => openTestRequest(req)}
        handleMultiTab={() => handleMultiTab(req)}
        requester={req.requester?.userName || ""}
        status={req.status}
        submissionDate={req.submissionOrApprovalDate || ""}
        testRequestId={req.requestId || ""}
        onPriorityChange={changeRequestPriority}
      />
    );
  };

  useEffect(() => {
    setIsReloading(true);
    sync(fetchTestRequests)
      .then((res) => {
        const newData = (res.data.data as LocalTestRequestSearchResult[]) || [];
        if (pageIndex === 0) {
          // First load or reset - replace all data
          setTestRequests(newData);
        } else {
          // Load more - append new data to existing
          setTestRequests((prevTestRequests) => [...prevTestRequests, ...newData]);
        }
        setTotalData(res.data.totalData || 0);
      })
      .catch(handleHttpError)
      .finally(() => {
        setIsLoading(false);
        setIsReloading(false);
      });
  }, [refresh, pageIndex, doReload, retriggerSearch]);

  useEffect(() => {
    setTestRequests([]);
    setPageIndex(0);
    setIsLoading(true); // Set loading state when clearing data
    setDoReload((prevDoReload) => prevDoReload + 1);
  }, [labOrUser, statusFilterQuery, basicSearchTerms, advancedSearch, waitingForCustomer]);

  if (isLoading && pageIndex === 0) {
    return <CustomProgress />;
  }

  if (!isLoading && testRequests?.length === 0) {
    return (
      <>
        <LinearIndeterminate loading={isReloading || waiting} />
        {!isReloading && !waiting && <Box marginTop={1}>No results found.</Box>}
      </>
    );
  }

  return (
    <>
      <LinearIndeterminate loading={isReloading || waiting} />
      <div className={classes.root}>
        <Grid container justify="space-between" direction="row" spacing={4}>
          <Grid className={classes.column} item lg={3} md={3} sm={3} xs={3}>
            <h3 className={classes.h3}>{requesterView ? "Draft / To Do" : "New Requests"}</h3>
            <div className={classes.cardContainer}>
              {testRequests
                ?.filter((req) =>
                  requesterView
                    ? // requester view
                      req.status.state === "DRAFT" ||
                      req.status.state === "FOR_REVIEW" ||
                      req.status.state === "FOR_APPROVAL"
                    : // standard view
                      req.status.state === "DRAFT" ||
                      req.status.state === "SUBMITTED" ||
                      req.status.state === "FOR_EVALUATION",
                )
                .map((req: LocalTestRequestSearchResult) => cardFromRequest(req))}
            </div>
          </Grid>
          <Grid className={classes.column} item lg={3} md={3} sm={3} xs={3}>
            <h3 className={classes.h3}>{requesterView ? "In Preparation" : "To Do"}</h3>
            <div className={classes.cardContainer}>
              {testRequests
                ?.filter((req) =>
                  requesterView
                    ? // requester view
                      req.status.state === "SUBMITTED" ||
                      req.status.state === "FOR_EVALUATION" ||
                      req.status.state === "IN_PREPARATION"
                    : // standard view
                      req.status.state === "FOR_REVIEW" ||
                      req.status.state === "FOR_APPROVAL" ||
                      req.status.state === "IN_PREPARATION",
                )
                .map((req: LocalTestRequestSearchResult) => cardFromRequest(req))}
            </div>
          </Grid>
          <Grid className={classes.column} item lg={3} md={3} sm={3} xs={3}>
            <h3 className={classes.h3}>In Execution</h3>
            <div className={classes.cardContainer}>
              {testRequests
                ?.filter((req) => req.status.state === "IN_EXECUTION" || req.status.state === "REPORT_CREATION")
                .map((req: LocalTestRequestSearchResult) => cardFromRequest(req))}
            </div>
          </Grid>
          <Grid className={classes.column} item lg={3} md={3} sm={3} xs={3}>
            <h3 className={classes.h3}>Closed within last 30 days</h3>
            <div className={classes.cardContainer}>
              {testRequests
                ?.filter(
                  (req) =>
                    req.status.state === "DECLINED" ||
                    req.status.state === "COMPLETED" ||
                    req.status.state === "CANCELED",
                )
                .map((req: LocalTestRequestSearchResult) => cardFromRequest(req))}
            </div>
          </Grid>
        </Grid>
        {showButtons && (
          <div className={classes.buttonOrSpinnerContainer}>
            {showLoadMoreButton ? (
              <CustomButton onClick={loadMore} text="Load More Cards" />
            ) : (
              showCollapseButton && <CustomButton onClick={collapse} text="Collapse" />
            )}
          </div>
        )}
        {isLoading && (
          <div className={classes.buttonOrSpinnerContainer}>
            <CircularProgress style={{ color: tesaBlue }} />
          </div>
        )}
      </div>
    </>
  );
};

export default TestRequestBoard;
